import { ACOES, type AcaoId } from '@acme/core'
import Grid from 'global/components/ui/Grid'
import { useWatch } from 'react-hook-form'
import { z } from 'zod'
import { ACOES_UI } from '../acoes'
import { CatalogoDialog, SeloIcone } from './CatalogoDialog'

const schema = z
    .object({
        acaoId: z.enum(Object.keys(ACOES) as [AcaoId, ...AcaoId[]]),
        parametros: z.record(z.unknown()),
    })
    .superRefine((acao, ctx) => {
        const resultado = ACOES[acao.acaoId].parametros.safeParse(
            acao.parametros,
        )
        if (resultado.success) return
        for (const issue of resultado.error.issues)
            ctx.addIssue({ ...issue, path: ['parametros', ...issue.path] })
    })

export type Acao = z.infer<typeof schema>

interface AcaoDialogProps {
    /** Ação em edição; ausente, o diálogo cria uma nova. */
    valor?: Acao
    onClose: () => void
    onSalvar: (acao: Acao) => void
}

/**
 * Sub-diálogo de uma ação da rotina: escolhe no catálogo e depois configura.
 * Salvar só devolve a ação pelo `onSalvar`.
 */
export function AcaoDialog({ valor, onClose, onSalvar }: AcaoDialogProps) {
    return (
        <CatalogoDialog
            titulo={valor ? 'Editar ação' : 'Adicionar ação'}
            item="ação"
            cor="info"
            catalogo={ACOES}
            ui={ACOES_UI}
            name="acaoId"
            iniciar={(id: AcaoId) => ({
                acaoId: id,
                parametros: ACOES_UI[id].parametrosIniciais,
            })}
            schema={schema}
            valor={valor}
            onClose={onClose}
            onSubmit={(acao: Acao) =>
                onSalvar({ acaoId: acao.acaoId, parametros: acao.parametros })
            }
        >
            <Grid container>
                <ParametrosAcao />
            </Grid>
        </CatalogoDialog>
    )
}

function ParametrosAcao() {
    const acaoId = useWatch({ name: 'acaoId' }) as AcaoId
    const { Parametros } = ACOES_UI[acaoId]

    return <Parametros />
}

/** Selo com o ícone da ação, usado na lista da rotina. */
export function IconeAcao({ acaoId }: { acaoId: AcaoId }) {
    return <SeloIcone icone={ACOES_UI[acaoId].icone} cor="info" />
}
